import { useAuthInfo } from '@propelauth/react';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'sonner';

import { APIBaseChronos } from '../hosts';
import { QueryEntity } from '../types';
import useGetFetchConfig, { FetchMethod } from '../useGetFetchConfig';
import { editFactInVisualisation } from '@/screens/Chronos/CaseEditor/DataView/ChronologyViewer/ChronologyEditor/VisualTimeline/SyncingHelpers/syncVisualisationWithTimelineChanges';
import { ChronologyFact } from '@/types';

interface UseUpdateChronologyFactWithSync {
  field: keyof ChronologyFact;
  value: ChronologyFact[keyof ChronologyFact];
  factId: string;
  chronologyId: string;
}

const useUpdateChronologyFactWithSync = ({ caseId }: { caseId: string }) => {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();
  const authInfo = useAuthInfo();

  return useMutation(
    async ({ field, value, factId, chronologyId }: UseUpdateChronologyFactWithSync) => {
      const fetchConfigPatch = getFetchConfig({ method: FetchMethod.PATCH, data: { [field]: value } });

      const response = await fetch(
        `${APIBaseChronos}/api/case/${caseId}/chronology/${chronologyId}/fact/${factId}`,
        fetchConfigPatch,
      );

      if (!response.ok) {
        toast.error('Failed to update field');
        throw new Error('Failed to update field');
      }

      const updatedFactFromDB = await response.json();

      return { updatedFact: updatedFactFromDB, chronologyId };
    },
    {
      onSuccess: async ({ updatedFact, chronologyId }) => {
        // Invalidate chronology query first
        queryClient.invalidateQueries({ queryKey: [QueryEntity.Chronology, { chronologyId }] });
        toast.success('Chronology fact updated');
        // Sync with visual timeline
        try {
          await editFactInVisualisation({
            caseId,
            chronologyId,
            updatedFact,
            getAccessToken: authInfo.tokens.getAccessToken,
          });

          queryClient.invalidateQueries([QueryEntity.ChronologyVisualisation, caseId, chronologyId]);
        } catch (error) {
          console.error('Failed to sync updated fact with visual timeline:', error);
        }
      },
      onError: (error) => {
        console.error('Update Error: ', error);
        toast.error('Failed to update field');
      },
    },
  );
};

export default useUpdateChronologyFactWithSync;
